import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { templatesApi, documentsApi, bullhornApi, getErrorMessage } from '../services/api';
import { 
  Send, 
  Search, 
  User,
  FileText,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  ArrowRight,
  ArrowLeft,
  Building2,
  Plus,
  X,
  Clock,
  FileCheck,
  Users,
  Trash2,
  Upload
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';

// Bullhorn entity colors (from Bullhorn official brand guidelines)
const ENTITY_COLORS = {
  candidate: { bg: 'bg-[#4CAF50]', text: 'text-white', label: 'Candidate', lightBg: 'bg-[#4CAF50]/10', border: 'border-[#4CAF50]', iconColor: 'text-[#4CAF50]' },
  contact: { bg: 'bg-[#F9A825]', text: 'text-white', label: 'Contact', lightBg: 'bg-[#F9A825]/10', border: 'border-[#F9A825]', iconColor: 'text-[#F9A825]' },
  placement: { bg: 'bg-[#1a4a5e]', text: 'text-white', label: 'Placement', lightBg: 'bg-[#1a4a5e]/10', border: 'border-[#1a4a5e]', iconColor: 'text-[#1a4a5e]' },
};

const STEPS = [
  { id: 'template', title: 'Select Template', icon: FileText },
  { id: 'recipients', title: 'Add Recipients', icon: Users },
  { id: 'options', title: 'Options', icon: Clock },
  { id: 'review', title: 'Review & Send', icon: Send },
];

export default function BulkSendPage() {
  const navigate = useNavigate();
  const { getTenantPath } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  
  // Selected template
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Recipients list
  const [recipients, setRecipients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  // Manual entry
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualRecipient, setManualRecipient] = useState({
    name: '',
    email: '',
    entity_type: 'candidate',
  });
  
  // Options
  const [expiryHours, setExpiryHours] = useState(72);
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [message, setMessage] = useState('');
  
  // Send progress
  const [sendProgress, setSendProgress] = useState({ sent: 0, total: 0, errors: [] });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await templatesApi.list();
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const entityType = selectedTemplate?.entity_type || 'candidate';
      const response = entityType === 'contact' 
        ? await bullhornApi.searchContacts(searchQuery)
        : await bullhornApi.searchCandidates(searchQuery);
      
      const results = entityType === 'contact' 
        ? response.data.contacts || []
        : response.data.candidates || [];
      
      // Filter out already added recipients
      const existingEmails = new Set(recipients.map(r => r.email.toLowerCase()));
      const filteredResults = results.filter(r => !existingEmails.has(r.email?.toLowerCase()));
      
      setSearchResults(filteredResults);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const addRecipient = (person) => {
    const entityType = selectedTemplate?.entity_type || 'candidate';
    setRecipients(prev => [...prev, {
      id: `${person.id || Date.now()}`,
      bullhorn_id: person.id ? String(person.id) : null,
      name: person.name,
      email: person.email,
      entity_type: entityType,
      title: person.title || '',
      company_id: person.companyId || '',
      company_name: person.companyName || '',
    }]);
    
    // Remove from search results
    setSearchResults(prev => prev.filter(r => r.id !== person.id));
    setSearchQuery('');
  };

  const addManualRecipient = () => {
    if (!manualRecipient.name || !manualRecipient.email) {
      toast.error('Name and email are required');
      return;
    }
    
    // Check for duplicate
    if (recipients.some(r => r.email.toLowerCase() === manualRecipient.email.toLowerCase())) {
      toast.error('Recipient already added');
      return;
    }
    
    setRecipients(prev => [...prev, {
      id: `manual_${Date.now()}`,
      bullhorn_id: null,
      name: manualRecipient.name,
      email: manualRecipient.email,
      entity_type: selectedTemplate?.entity_type || 'candidate',
    }]);
    
    setManualRecipient({ name: '', email: '', entity_type: 'candidate' });
    setShowManualEntry(false);
  };

  const removeRecipient = (id) => {
    setRecipients(prev => prev.filter(r => r.id !== id));
  };

  const handleBulkSend = async () => {
    if (!selectedTemplate || recipients.length === 0) return;
    
    setSending(true);
    setSendProgress({ sent: 0, total: recipients.length, errors: [] });
    
    const errors = [];
    let sent = 0;
    
    for (const recipient of recipients) {
      try {
        await documentsApi.send({
          template_id: selectedTemplate.id,
          recipient: {
            bullhorn_id: recipient.bullhorn_id,
            name: recipient.name,
            email: recipient.email,
            entity_type: recipient.entity_type,
            title: recipient.title,
            company_id: recipient.company_id,
            company_name: recipient.company_name,
          },
          expiry_hours: expiryHours,
          message: message || null,
          requires_approval: requiresApproval,
        });
        sent++;
      } catch (error) {
        errors.push({ recipient: recipient.email, error: getErrorMessage(error, 'Send failed') });
      }
      setSendProgress({ sent, total: recipients.length, errors });
    }
    
    setSending(false);
    
    if (errors.length === 0) {
      toast.success(`Successfully sent ${sent} documents!`);
      navigate(getTenantPath('/documents?highlight=latest'));
    } else if (sent > 0) {
      toast.warning(`Sent ${sent} of ${recipients.length}. ${errors.length} failed.`);
    } else {
      toast.error('All sends failed');
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return !!selectedTemplate;
      case 1: return recipients.length > 0;
      case 2: return true;
      case 3: return !sending;
      default: return false;
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return renderTemplateSelection();
      case 1:
        return renderRecipientsSelection();
      case 2:
        return renderOptions();
      case 3:
        return renderReview();
      default:
        return null;
    }
  };

  const renderTemplateSelection = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold mb-2">Select a Template</h2>
        <p className="text-muted-foreground text-sm">
          Choose the document template to send to multiple recipients
        </p>
      </div>
      
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : templates.length > 0 ? (
        (() => {
          // Same 3-column entity-grouped + alphabetical layout as the
          // single-send template picker and the Templates page, so
          // recruiters use one mental model across every surface.
          const groups = { candidate: [], placement: [], contact: [] };
          for (const t of templates) {
            const k = (t.entity_type || 'candidate');
            (groups[k] || groups.candidate).push(t);
          }
          for (const k of Object.keys(groups)) {
            groups[k].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
          }
          const cols = [
            { key: 'candidate', label: 'Candidate', items: groups.candidate },
            { key: 'placement', label: 'Placement', items: groups.placement },
            { key: 'contact', label: 'Contact', items: groups.contact },
          ];
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {cols.map((col) => {
                const ec = ENTITY_COLORS[col.key];
                return (
                  <div key={col.key} data-testid={`bulk-template-col-${col.key}`}>
                    <div className="flex items-center gap-2 mb-2 px-1">
                      <span className={`inline-block w-2 h-2 rounded-full ${ec.bg}`} />
                      <h3 className="text-sm font-semibold text-slate-700">{col.label} Documents</h3>
                      <span className="text-xs text-muted-foreground">({col.items.length})</span>
                    </div>
                    {col.items.length === 0 ? (
                      <div className="rounded-lg border border-dashed border-slate-200 p-4 text-center text-xs text-muted-foreground">
                        No {col.label.toLowerCase()} documents
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        {col.items.map((template) => {
                          const isSelected = selectedTemplate?.id === template.id;
                          return (
                            <div
                              key={template.id}
                              onClick={() => setSelectedTemplate(template)}
                              className={`px-3 py-2 rounded-lg border cursor-pointer transition-all duration-150 overflow-hidden ${
                                isSelected ? `${ec.border} shadow-sm` : 'border-border hover:border-primary/40 bg-white'
                              }`}
                              data-testid={`template-card-${template.id}`}
                            >
                              <div className="flex items-center gap-2">
                                <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${isSelected ? ec.bg : ec.lightBg}`}>
                                  <FileText className={`w-3 h-3 ${isSelected ? 'text-white' : ec.iconColor}`} />
                                </div>
                                <h4 className="font-medium text-sm truncate flex-1 min-w-0" title={template.name}>
                                  {template.name}
                                </h4>
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${ec.bg} ${ec.text} flex-shrink-0 whitespace-nowrap`}>
                                  {ec.label}
                                </span>
                                {isSelected && <CheckCircle2 className={`w-4 h-4 flex-shrink-0 ${ec.iconColor}`} />}
                              </div>
                              <div className="text-[11px] text-muted-foreground mt-0.5 pl-8">
                                {template.fields?.length || 0} fields
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()
      ) : (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <p className="text-muted-foreground">No templates available</p>
          <Button className="mt-4" onClick={() => navigate(getTenantPath('/templates'))}>
            Create Template
          </Button>
        </div>
      )}
    </div>
  );

  const renderRecipientsSelection = () => {
    const entityType = selectedTemplate?.entity_type || 'candidate';
    const entityColors = ENTITY_COLORS[entityType];
    
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-bold mb-2">Add Recipients</h2>
          <p className="text-muted-foreground text-sm">
            Add multiple {entityColors.label.toLowerCase()}s to receive "{selectedTemplate?.name}"
          </p>
        </div>
        
        {/* Search Section */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Search Bullhorn</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder={`Search ${entityColors.label.toLowerCase()}s...`}
                  className="pl-10"
                  data-testid="search-recipients-input"
                />
              </div>
              <Button onClick={handleSearch} disabled={searching}>
                {searching ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Search'}
              </Button>
            </div>
            
            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="mt-4 max-h-48 overflow-auto border rounded-lg">
                {searchResults.map((person) => (
                  <div
                    key={person.id}
                    className="flex items-center justify-between p-3 border-b last:border-b-0 hover:bg-muted/50"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full ${entityColors.lightBg} flex items-center justify-center`}>
                        {entityType === 'contact' ? (
                          <Building2 className="w-4 h-4 text-amber-600" />
                        ) : (
                          <User className="w-4 h-4 text-green-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{person.name}</p>
                        <p className="text-xs text-muted-foreground">{person.email}</p>
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => addRecipient(person)}>
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Manual Entry */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium">Manual Entry</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowManualEntry(!showManualEntry)}>
                {showManualEntry ? 'Cancel' : <><Plus className="w-4 h-4 mr-1" /> Add Manually</>}
              </Button>
            </div>
          </CardHeader>
          {showManualEntry && (
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs">Name *</Label>
                  <Input
                    value={manualRecipient.name}
                    onChange={(e) => setManualRecipient(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Full Name"
                    data-testid="manual-name-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Email *</Label>
                  <Input
                    type="email"
                    value={manualRecipient.email}
                    onChange={(e) => setManualRecipient(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="email@example.com"
                    data-testid="manual-email-input"
                  />
                </div>
                <div className="flex items-end">
                  <Button onClick={addManualRecipient} className="w-full">
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
              </div>
            </CardContent>
          )}
        </Card>
        
        {/* Recipients List */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium">
                Recipients ({recipients.length})
              </CardTitle>
              {recipients.length > 0 && (
                <Button variant="ghost" size="sm" className="text-red-500" onClick={() => setRecipients([])}>
                  Clear All
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {recipients.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-auto">
                {recipients.map((recipient) => (
                  <div
                    key={recipient.id}
                    className={`flex items-center justify-between p-3 rounded-lg border ${entityColors.lightBg}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full ${entityColors.bg} flex items-center justify-center`}>
                        {entityType === 'contact' ? (
                          <Building2 className="w-4 h-4 text-white" />
                        ) : (
                          <User className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{recipient.name}</p>
                        <p className="text-xs text-muted-foreground">{recipient.email}</p>
                      </div>
                      {recipient.bullhorn_id && (
                        <span className="text-xs bg-white/50 px-2 py-0.5 rounded">
                          BH #{recipient.bullhorn_id}
                        </span>
                      )}
                    </div>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="text-red-500 hover:text-red-700"
                      onClick={() => removeRecipient(recipient.id)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No recipients added yet</p>
                <p className="text-xs mt-1">Search Bullhorn or add manually</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderOptions = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold mb-2">Send Options</h2>
        <p className="text-muted-foreground text-sm">
          Configure options for all {recipients.length} documents
        </p>
      </div>
      
      <Card>
        <CardContent className="pt-6 space-y-6">
          {/* Expiry */}
          <div>
            <Label className="text-sm font-medium">Link Expiry</Label>
            <div className="grid grid-cols-4 gap-2 mt-2">
              {[24, 48, 72, 168].map((hours) => (
                <Button
                  key={hours}
                  variant={expiryHours === hours ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setExpiryHours(hours)}
                >
                  {hours < 48 ? `${hours}h` : `${hours / 24}d`}
                </Button>
              ))}
            </div>
          </div>
          
          {/* Requires Approval */}
          <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/20">
            <div>
              <p className="font-medium text-sm">Require Admin Approval</p>
              <p className="text-xs text-muted-foreground">
                Documents will need approval before syncing to Bullhorn
              </p>
            </div>
            <Checkbox
              checked={requiresApproval}
              onCheckedChange={(checked) => setRequiresApproval(!!checked)}
              data-testid="requires-approval-checkbox"
            />
          </div>
          
          {/* Message */}
          <div>
            <Label className="text-sm font-medium">Custom Message (Optional)</Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add a personal message to include in the email..."
              className="mt-2 min-h-[100px]"
              data-testid="message-textarea"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderReview = () => {
    const entityColors = ENTITY_COLORS[selectedTemplate?.entity_type || 'candidate'];
    
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-bold mb-2">Review & Send</h2>
          <p className="text-muted-foreground text-sm">
            Confirm the details before sending to {recipients.length} recipients
          </p>
        </div>
        
        {/* Summary Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Send Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between py-2 border-b">
              <span className="text-sm text-muted-foreground">Template</span>
              <span className="font-medium text-sm">{selectedTemplate?.name}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b">
              <span className="text-sm text-muted-foreground">Recipients</span>
              <span className={`font-medium text-sm ${entityColors.bg.replace('bg-', 'text-').replace('[', '').replace(']', '')}`}>
                {recipients.length} {entityColors.label.toLowerCase()}s
              </span>
            </div>
            <div className="flex items-center justify-between py-2 border-b">
              <span className="text-sm text-muted-foreground">Link Expiry</span>
              <span className="font-medium text-sm">
                {expiryHours < 48 ? `${expiryHours} hours` : `${expiryHours / 24} days`}
              </span>
            </div>
            <div className="flex items-center justify-between py-2 border-b">
              <span className="text-sm text-muted-foreground">Requires Approval</span>
              <span className={`font-medium text-sm ${requiresApproval ? 'text-amber-600' : 'text-muted-foreground'}`}>
                {requiresApproval ? 'Yes' : 'No'}
              </span>
            </div>
            {message && (
              <div className="py-2">
                <span className="text-sm text-muted-foreground block mb-2">Custom Message</span>
                <p className="text-sm bg-muted/50 p-3 rounded">{message}</p>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Recipients Preview */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Recipients Preview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-48 overflow-auto space-y-1">
              {recipients.map((r, idx) => (
                <div key={r.id} className="flex items-center gap-2 text-sm py-1">
                  <span className="text-muted-foreground w-6">{idx + 1}.</span>
                  <span className="font-medium">{r.name}</span>
                  <span className="text-muted-foreground">({r.email})</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        
        {/* Send Progress (when sending) */}
        {sending && (
          <Card className="border-primary">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <RefreshCw className="w-6 h-6 animate-spin text-primary" />
                <div className="flex-1">
                  <p className="font-medium">Sending documents...</p>
                  <p className="text-sm text-muted-foreground">
                    {sendProgress.sent} of {sendProgress.total} sent
                  </p>
                  <div className="w-full bg-muted rounded-full h-2 mt-2">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all"
                      style={{ width: `${(sendProgress.sent / sendProgress.total) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
              {sendProgress.errors.length > 0 && (
                <div className="mt-4 p-3 bg-red-50 rounded-lg">
                  <p className="text-sm font-medium text-red-700">Errors ({sendProgress.errors.length}):</p>
                  {sendProgress.errors.map((e) => (
                    <p key={`${e.recipient}-${e.error}`} className="text-xs text-red-600">{e.recipient}: {e.error}</p>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="bulk-send-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Bulk Send</h1>
          <p className="text-muted-foreground font-body mt-1">
            Send the same document to multiple recipients at once
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8">
          {STEPS.map((step, index) => {
            const Icon = step.icon;
            const isActive = index === currentStep;
            const isComplete = index < currentStep;
            
            return (
              <div key={step.id} className="flex items-center flex-1">
                <div className="flex flex-col items-center">
                  <div className={`
                    w-10 h-10 rounded-full flex items-center justify-center transition-all
                    ${isComplete ? 'bg-success text-white' : 
                      isActive ? 'bg-primary text-white' : 
                      'bg-muted text-muted-foreground'}
                  `}>
                    {isComplete ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <span className={`text-xs mt-2 ${isActive ? 'font-medium' : 'text-muted-foreground'}`}>
                    {step.title}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-4 ${isComplete ? 'bg-success' : 'bg-muted'}`} />
                )}
              </div>
            );
          })}
        </div>

        {/* Step Content */}
        <div className="neumorphic rounded-xl p-6">
          {renderStep()}
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => setCurrentStep(prev => prev - 1)}
            disabled={currentStep === 0}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          {currentStep < STEPS.length - 1 ? (
            <Button
              onClick={() => setCurrentStep(prev => prev + 1)}
              disabled={!canProceed()}
              data-testid="next-step-button"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleBulkSend}
              disabled={!canProceed() || sending}
              className="bg-success hover:bg-success/90"
              data-testid="bulk-send-button"
            >
              {sending ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send to {recipients.length} Recipients
            </Button>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
